import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Plus, 
  Trash2, 
  Edit, 
  Calendar, 
  Tag, 
  Save, 
  X, 
  Search, 
  Filter, 
  AlertCircle, 
  Star, 
  Lock, 
  Unlock 
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { SearchInput } from '../common/SearchInput';

interface LegacyNote {
  id: string;
  title: string;
  content: string;
  date: string;
  category: string;
  tags?: string[];
  is_private: boolean;
  is_important: boolean;
}

export function Notes() {
  const { user } = useAuth();
  const [notes, setNotes] = useState<LegacyNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState<LegacyNote | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    date: new Date().toISOString().split('T')[0],
    category: 'wishes',
    tags: [''],
    is_private: false,
    is_important: false
  });

  // Note categories
  const categories = [
    { id: 'wishes', label: 'Wishes & Requests' },
    { id: 'values', label: 'Values & Beliefs' },
    { id: 'advice', label: 'Advice & Wisdom' },
    { id: 'legacy', label: 'Legacy Planning' },
    { id: 'personal', label: 'Personal Reflections' },
    { id: 'other', label: 'Other' }
  ];

  // Common tags
  const commonTags = [
    'important', 'personal', 'family', 'financial', 'healthcare', 
    'funeral', 'digital', 'property', 'wishes', 'values'
  ];

  useEffect(() => {
    const loadNotes = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        // Load notes from database
        const { data, error } = await supabase
          .from('legacy_notes')
          .select('*')
          .eq('user_id', user.id)
          .order('date', { ascending: false });
          
        if (error) throw error;
        
        setNotes(data || []);
      } catch (err) {
        console.error('Error loading notes:', err);
        setError(err instanceof Error ? err.message : 'Failed to load notes');
      } finally {
        setLoading(false);
      }
    };
    
    loadNotes();
  }, [user]);

  const handleAddNote = () => {
    setFormData({
      title: '',
      content: '',
      date: new Date().toISOString().split('T')[0],
      category: 'wishes',
      tags: [''],
      is_private: false,
      is_important: false
    });
    setIsEditing(false);
    setShowAddModal(true);
  };

  const handleEditNote = (note: LegacyNote) => {
    setSelectedNote(note);
    setFormData({
      title: note.title,
      content: note.content,
      date: note.date,
      category: note.category,
      tags: note.tags || [''],
      is_private: note.is_private,
      is_important: note.is_important
    });
    setIsEditing(true);
    setShowAddModal(true);
  };

  const handleViewNote = (note: LegacyNote) => {
    setSelectedNote(note);
    setShowViewModal(true);
  };

  const handleDeleteNote = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this note? This action cannot be undone.')) {
      try {
        const { error } = await supabase
          .from('legacy_notes')
          .delete()
          .eq('id', id);
          
        if (error) throw error;
        
        setNotes(notes.filter(note => note.id !== id));
      } catch (err) {
        console.error('Error deleting note:', err);
        setError(err instanceof Error ? err.message : 'Failed to delete note');
      }
    }
  };

  const handleAddTag = () => {
    setFormData({
      ...formData,
      tags: [...formData.tags, '']
    });
  };

  const handleRemoveTag = (index: number) => {
    const newTags = [...formData.tags];
    newTags.splice(index, 1);
    setFormData({
      ...formData,
      tags: newTags
    });
  };

  const handleTagChange = (index: number, value: string) => {
    const newTags = [...formData.tags];
    newTags[index] = value;
    setFormData({
      ...formData,
      tags: newTags
    });
  };

  const handleSaveNote = async () => {
    // Validate form
    if (!formData.title || !formData.content || !formData.date || !formData.category) {
      setError('Please fill in all required fields');
      return;
    }
    
    // Filter out empty tags
    const filteredTags = formData.tags.filter(tag => tag.trim() !== '');
    
    try {
      if (isEditing && selectedNote) {
        // Update existing note
        const { error } = await supabase
          .from('legacy_notes')
          .update({
            title: formData.title,
            content: formData.content,
            date: formData.date,
            category: formData.category,
            tags: filteredTags.length > 0 ? filteredTags : undefined,
            is_private: formData.is_private,
            is_important: formData.is_important
          })
          .eq('id', selectedNote.id);
          
        if (error) throw error;
        
        // Update local state
        setNotes(notes.map(note => 
          note.id === selectedNote.id 
            ? {
                ...note,
                title: formData.title,
                content: formData.content,
                date: formData.date,
                category: formData.category,
                tags: filteredTags.length > 0 ? filteredTags : undefined,
                is_private: formData.is_private,
                is_important: formData.is_important
              } 
            : note
        ));
      } else {
        // Add new note
        const { data, error } = await supabase
          .from('legacy_notes')
          .insert([{
            user_id: user?.id,
            title: formData.title,
            content: formData.content,
            date: formData.date,
            category: formData.category,
            tags: filteredTags.length > 0 ? filteredTags : undefined,
            is_private: formData.is_private,
            is_important: formData.is_important
          }])
          .select()
          .single();
          
        if (error) throw error;
        
        setNotes([data, ...notes]);
      }
      
      // Close modal and reset form
      setShowAddModal(false);
      setSelectedNote(null);
      setIsEditing(false);
      setError(null);
    } catch (err) {
      console.error('Error saving note:', err);
      setError(err instanceof Error ? err.message : 'Failed to save note');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryLabel = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.label : categoryId;
  };

  // Get all unique tags from notes
  const allTags = Array.from(new Set(
    notes.flatMap(note => note.tags || [])
  )).sort();

  // Filter notes based on search query and selected category
  const filteredNotes = notes.filter(note => {
    const matchesSearch = 
      note.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      note.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (note.tags && note.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())));
    
    const matchesCategory = selectedCategory === 'all' || note.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
        <div className="h-12 bg-gray-200 rounded"></div>
        <div className="h-64 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Legacy Notes</h1>
          <p className="text-gray-600 mt-1">Document important wishes, values, and information</p>
        </div>
        <Button
          variant="primary"
          onClick={handleAddNote}
          icon={<Plus size={20} />}
        >
          Add Note
        </Button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} />
          <p>{error}</p>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-4">
        <div className="w-full md:w-64 space-y-4">
          <Card>
            <div className="p-4">
              <h2 className="font-medium text-gray-900 mb-3">Categories</h2>
              <div className="space-y-1">
                <button
                  onClick={() => setSelectedCategory('all')}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    selectedCategory === 'all' ? 'bg-green-100 text-green-800' : 'hover:bg-gray-100'
                  }`}
                >
                  All Notes
                </button>
                {categories.map(category => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      selectedCategory === category.id ? 'bg-green-100 text-green-800' : 'hover:bg-gray-100'
                    }`}
                  >
                    {category.label}
                  </button>
                ))}
              </div>
            </div>
          </Card>
          
          <Card>
            <div className="p-4">
              <h2 className="font-medium text-gray-900 mb-3">Tags</h2>
              <div className="flex flex-wrap gap-2">
                {allTags.map(tag => (
                  <button
                    key={tag}
                    onClick={() => setSearchQuery(tag)}
                    className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded-full transition-colors"
                  >
                    #{tag}
                  </button>
                ))}
                {allTags.length === 0 && (
                  <p className="text-sm text-gray-500">No tags yet</p>
                )}
              </div>
            </div>
          </Card>
          
          <Card>
            <div className="p-4">
              <h2 className="font-medium text-gray-900 mb-3">Note Stats</h2>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Notes:</span>
                  <span className="font-medium">{notes.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Important:</span>
                  <span className="font-medium">
                    {notes.filter(note => note.is_important).length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Private:</span>
                  <span className="font-medium">
                    {notes.filter(note => note.is_private).length}
                  </span>
                </div>
              </div>
            </div>
          </Card>
        </div>
        
        <div className="flex-1">
          <Card>
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Legacy Notes</h2>
                <SearchInput
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search notes..."
                  className="w-48 md:w-64"
                />
              </div>
              
              {filteredNotes.length > 0 ? (
                <div className="space-y-4">
                  {filteredNotes.map(note => (
                    <div 
                      key={note.id} 
                      className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors cursor-pointer"
                      onClick={() => handleViewNote(note)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex items-start gap-4">
                          <div className="p-2 bg-green-100 rounded-lg flex-shrink-0 mt-1">
                            <FileText className="text-green-600" size={20} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium text-gray-900">{note.title}</h3>
                              {note.is_important && (
                                <Star className="text-amber-500" size={16} />
                              )}
                              {note.is_private && (
                                <Lock className="text-gray-500" size={16} />
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                              {getCategoryLabel(note.category)} • {formatDate(note.date)}
                            </p>
                            <p className="text-sm text-gray-600 mt-2 line-clamp-2">{note.content}</p>
                            
                            {note.tags && note.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {note.tags.map((tag, index) => (
                                  <span key={index} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">
                                    #{tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditNote(note);
                            }}
                            className="p-2 text-gray-400 hover:text-green-600 rounded-full hover:bg-green-50"
                            title="Edit note"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteNote(note.id);
                            }}
                            className="p-2 text-gray-400 hover:text-red-600 rounded-full hover:bg-red-50"
                            title="Delete note"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No notes found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {searchQuery || selectedCategory !== 'all'
                      ? 'No notes match your search or filter'
                      : 'Get started by creating your first legacy note'}
                  </p>
                  {!searchQuery && selectedCategory === 'all' && (
                    <div className="mt-6">
                      <Button
                        variant="primary"
                        onClick={handleAddNote}
                        icon={<Plus size={20} />}
                      >
                        Add Note
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Add/Edit Note Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={isEditing ? "Edit Legacy Note" : "New Legacy Note"}
        maxWidth="2xl"
      >
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title*
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-500"
              placeholder="Enter a title for your note"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category*
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                required
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date*
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full p-2 pl-9 border rounded-lg focus:ring-2 focus:ring-green-500"
                  required
                />
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note Content*
            </label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              rows={8}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-500"
              placeholder="Write your note here..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags
            </label>
            {formData.tags.map((tag, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={tag}
                    onChange={(e) => handleTagChange(index, e.target.value)}
                    className="w-full p-2 pl-8 border rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Enter a tag (e.g., important, wishes)"
                    list="common-tags"
                  />
                  <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <datalist id="common-tags">
                    {commonTags.map(tag => (
                      <option key={tag} value={tag} />
                    ))}
                  </datalist>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveTag(index)}
                  className="p-2 text-gray-400 hover:text-red-600 rounded"
                  disabled={formData.tags.length <= 1 && index === 0}
                >
                  <Trash2 size={20} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddTag}
              className="mt-2 text-sm text-green-600 hover:text-green-800"
            >
              + Add Another Tag
            </button>
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_important}
                onChange={(e) => setFormData({ ...formData, is_important: e.target.checked })}
                className="rounded text-amber-500 focus:ring-amber-500"
              />
              <div className="flex items-center gap-1">
                <Star size={16} className="text-amber-500" />
                <span className="text-sm text-gray-700">Mark as Important</span>
              </div>
            </label>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_private}
                onChange={(e) => setFormData({ ...formData, is_private: e.target.checked })}
                className="rounded text-gray-500 focus:ring-gray-500"
              />
              <div className="flex items-center gap-1">
                <Lock size={16} className="text-gray-500" />
                <span className="text-sm text-gray-700">Mark as Private</span>
              </div>
            </label>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowAddModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveNote}
              icon={<Save size={16} />}
            >
              {isEditing ? 'Update Note' : 'Save Note'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* View Note Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title={selectedNote?.title || "Legacy Note"}
        maxWidth="2xl"
      >
        {selectedNote && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <span className="text-sm bg-green-100 text-green-800 px-2 py-0.5 rounded">
                  {getCategoryLabel(selectedNote.category)}
                </span>
                <span className="text-sm text-gray-500">{formatDate(selectedNote.date)}</span>
                {selectedNote.is_important && (
                  <span className="flex items-center gap-1 text-sm text-amber-600">
                    <Star size={14} />
                    <span>Important</span>
                  </span>
                )}
                {selectedNote.is_private && (
                  <span className="flex items-center gap-1 text-sm text-gray-600">
                    <Lock size={14} />
                    <span>Private</span>
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  icon={<Edit size={14} />}
                  onClick={() => {
                    setShowViewModal(false);
                    handleEditNote(selectedNote);
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  color="danger"
                  icon={<Trash2 size={14} />}
                  onClick={() => {
                    setShowViewModal(false);
                    handleDeleteNote(selectedNote.id);
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="prose max-w-none">
                {selectedNote.content.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="mb-4 last:mb-0">{paragraph}</p>
                ))}
              </div>
            </div>
            
            {selectedNote.tags && selectedNote.tags.length > 0 && (
              <div className="flex items-center gap-2">
                <Tag size={16} className="text-gray-500" />
                <div className="flex flex-wrap gap-1">
                  {selectedNote.tags.map((tag, index) => (
                    <span key={index} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}